import { useStore } from 'react-redux'
import PropTypes from 'prop-types'
import React from 'react'
import cc from 'classcat'
import loadable from '@loadable/component'

import { fetchVimeoThumbnail } from '../../../store/actions'
import { imageUrl } from '../../ThemeView'
import { useFetchedData } from '../../../utils/hooks/useFetchedData'
import LazyImage from '../../LazyImage'
import Swiper from '../../templateComponents/Workspace/plugins/contentslider/Swiper'
import VideoSlide from '../../templateComponents/Workspace/plugins/components/VideoComponent/VideoSlide'

export const ZoomableImage = loadable(() => import(/* webpackChunkName: "Product" */ '../../ZoomableImage'))

const ImageSlide = ({ slide: image, productName }) => {
  const { url, width, height } = image

  return (
    <React.Fragment>
      {image.isZoomEnabled ? (
        <ZoomableImage
          src={url}
          width={width}
          height={height}
          alt={productName}
          imageContainerSelector={'.product-image-gallery'}
          enlargedImagePortalId={'product-image-gallery-zoom'}
        />
      ) : (
        <LazyImage src={url} alt={image.alt} originalWidth={width} style={{ maxWidth: width }} noscript={false} />
      )}
      <noscript>
        <img src={imageUrl(url, 600, 2560)} alt={image.alt} style={{ maxWidth: width }} itemProp="image" />
      </noscript>
    </React.Fragment>
  )
}

ImageSlide.propTypes = {
  slide: PropTypes.object.isRequired,
  productName: PropTypes.string,
}

const Slide = ({ slide, isInView, productName }) => {
  return (
    <div className="swiper-slide">
      {slide.type === 'image' && <ImageSlide {...{ slide, productName }} />}
      {(slide.type === 'YouTube' || slide.type === 'Vimeo') && <VideoSlide {...{ slide, isInView }} />}
    </div>
  )
}

Slide.propTypes = {
  slide: PropTypes.object.isRequired,
  isInView: PropTypes.bool.isRequired,
  productName: PropTypes.string,
}

const ImageGalleryBullets = ({ slides, slideTo, slideIdx }) => (
  <div className="product-image-gallery-bullets">
    {slides.map((slide, index) => (
      <div
        key={index}
        className={cc(['product-image-gallery-bullet', { active: slide.index === slideIdx }])}
        onClick={() => slideTo(slide.index)}
      />
    ))}
  </div>
)

ImageGalleryBullets.propTypes = {
  slides: PropTypes.array.isRequired,
  slideTo: PropTypes.func.isRequired,
  slideIdx: PropTypes.number.isRequired,
}

const ImageGalleryArrows = ({ nextButtonRef, prevButtonRef }) => (
  <div className="swiper-arrows">
    <div ref={nextButtonRef} className="swiper-arrow-right" />
    <div ref={prevButtonRef} className="swiper-arrow-left swiper-button-disabled" />
  </div>
)

ImageGalleryArrows.propTypes = {
  nextButtonRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  prevButtonRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
}

const Thumbnail = ({ slide }) => {
  const { api } = useStore()
  const [thumbnail] = useFetchedData(
    async () => {
      if (slide.previewImageUrl) return slide.previewImageUrl
      if (slide.type === 'Vimeo') return fetchVimeoThumbnail(slide.url, api)
      return ''
    },
    [slide],
    slide.previewImageUrl,
  )

  return (
    <div
      className={cc(['product-image-gallery-thumbnail-inner'], {
        'video-thumbnail': slide.type !== `image`,
      })}
      style={{
        backgroundImage: `url("${
          slide.type === `image` ? imageUrl(slide.url) : thumbnail && imageUrl(thumbnail, 424, 424)
        }")`,
      }}
    />
  )
}

Thumbnail.propTypes = {
  slide: PropTypes.object.isRequired,
}

const SwiperMainSlideContainer = ({
  slides,
  slideIdx,
  productName,
  slideNext,
  thumbnailListIsVertical,
  swiperContainerRef,
}) => (
  <div className="swiper-image-aspect-ratio">
    <div className="swiper-container swiper-main-image" ref={swiperContainerRef}>
      <div className="swiper-wrapper" onClick={() => !thumbnailListIsVertical && slideNext()}>
        {slides.map((slide, index) => (
          <Slide key={index} isInView={slideIdx === index} {...{ slide, productName, swiperContainerRef }} />
        ))}
      </div>
    </div>
  </div>
)

SwiperMainSlideContainer.propTypes = {
  slides: PropTypes.array.isRequired,
  slideIdx: PropTypes.number.isRequired,
  productName: PropTypes.string.isRequired,
  slideNext: PropTypes.func.isRequired,
  thumbnailListIsVertical: PropTypes.bool,
  swiperContainerRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
}

const basicSliderSettings = {
  loop: false,
  keyboard: { enabled: true },
  grabCursor: true,
}

const visionThemeSliderSettings = {
  customControlClass: 'swiper-thumbnails',
  controller: { by: 'container' },
  zoom: { enabled: true },
}

export default function ImageGallery({ product, thumbnailListIsVertical }) {
  const swiperContainerRef = React.useRef(null)
  const nextButtonRef = React.useRef(null)
  const prevButtonRef = React.useRef(null)

  const sliderSettings = Object.assign(basicSliderSettings, thumbnailListIsVertical ? visionThemeSliderSettings : {})

  const renderVerticalThumbnails = ({ slides, slideIdx, ready, slideTo }) => {
    return (
      <div className="product-image-gallery-thumbnails">
        <Swiper
          customControlled={true}
          slides={slides}
          direction={'vertical'}
          spaceBetween={0}
          slidesPerView={'auto'}
          centeredSlides={false}
          refSet={{ swiperContainerRef, nextButtonRef, prevButtonRef }}
          mousewheel={{ enabled: slides.length > 4 }}
          loop={false}
          renderSwiper={({ slides }) => (
            <React.Fragment>
              {slides.length > 4 && <div ref={prevButtonRef} className="swiper-arrow-top swiper-button-disabled" />}
              <div className="swiper-container swiper-thumbnails" ref={swiperContainerRef}>
                <div className="swiper-wrapper">
                  {slides.map((slide, index) => (
                    <div
                      key={index}
                      className={cc([
                        'swiper-slide',
                        'product-image-gallery-thumbnail-outer',
                        {
                          active: ready && slide.index === slideIdx,
                        },
                      ])}
                      onClick={() => slideTo(slide.index)}
                    >
                      <Thumbnail {...{ slide }} />
                    </div>
                  ))}
                </div>
              </div>
              {slides.length > 4 && <div ref={nextButtonRef} className="swiper-arrow-bottom" />}
            </React.Fragment>
          )}
        />
      </div>
    )
  }

  return (
    <div className="product-image-gallery">
      <div className="product-image-gallery-zoom-container">
        <div id="product-image-gallery-zoom" />
      </div>
      <Swiper
        key={product.productId}
        slides={product.slideshow}
        {...sliderSettings}
        refSet={{ swiperContainerRef, nextButtonRef, prevButtonRef }}
        renderSwiper={({ slides, slideIdx, ready, slideNext, slideTo }) => (
          <React.Fragment>
            {thumbnailListIsVertical && renderVerticalThumbnails({ slides, slideIdx, ready, slideTo })}
            <SwiperMainSlideContainer
              {...{ slides, slideIdx, slideNext, thumbnailListIsVertical, swiperContainerRef }}
              productName={product.name}
            />
            <ImageGalleryBullets {...{ slides, slideTo, slideIdx }} />
            <ImageGalleryArrows {...{ nextButtonRef, prevButtonRef }} />
            {!thumbnailListIsVertical && (
              <div className="product-image-gallery-thumbnails">
                {slides.map((slide, index) => (
                  <div
                    key={index}
                    className={cc(['product-image-gallery-thumbnail-outer', { active: slide.index === slideIdx }])}
                    onClick={() => slideTo(slide.index)}
                  >
                    <Thumbnail {...{ slide }} />
                  </div>
                ))}
              </div>
            )}
          </React.Fragment>
        )}
      />
    </div>
  )
}

ImageGallery.propTypes = {
  product: PropTypes.object.isRequired,
  thumbnailListIsVertical: PropTypes.bool,
  slides: PropTypes.array,
  slideIdx: PropTypes.number,
  ready: PropTypes.bool,
  slideTo: PropTypes.func,
}
